<template>
  <themed-modal
    name="document-edit-modal"
    title="Edit Document"
    size="md"
    :click-to-close="!isSaving"
    @opened="handleOpened"
    @closed="handleClosed"
  >
    <form @submit.prevent="handleSubmit" class="document-edit-form">
      <!-- Document Name -->
      <div class="form-group">
        <label for="document-name" class="form-label required">
          Document Name
        </label>
        <input
          id="document-name"
          ref="nameInput"
          v-model="form.name"
          type="text"
          class="form-input"
          placeholder="Enter document name"
          :disabled="isSaving"
          aria-required="true"
        />
        <p v-if="errors.name" class="form-error" role="alert">
          {{ errors.name }}
        </p>
      </div>

      <!-- Category Dropdown -->
      <div class="form-group">
        <label for="edit-document-category" class="form-label required">
          Category
        </label>
        <select
          id="edit-document-category"
          v-model="form.category_id"
          class="form-select"
          :disabled="isSaving"
          aria-required="true"
          @change="handleCategoryChange"
        >
          <option :value="null">Select a category</option>
          <option
            v-for="category in categories"
            :key="category.id"
            :value="category.id"
          >
            {{ category.name }}
          </option>
        </select>
        <p v-if="errors.category_id" class="form-error" role="alert">
          {{ errors.category_id }}
        </p>
      </div>

      <!-- Subcategory Dropdown -->
      <div class="form-group">
        <label for="edit-document-subcategory" class="form-label">
          Subcategory (Optional)
        </label>
        <select
          id="edit-document-subcategory"
          v-model="form.subcategory_id"
          class="form-select"
          :disabled="isSaving || !form.category_id || filteredSubcategories.length === 0"
        >
          <option :value="null">Select a subcategory</option>
          <option
            v-for="subcategory in filteredSubcategories"
            :key="subcategory.id"
            :value="subcategory.id"
          >
            {{ subcategory.name }}
          </option>
        </select>
        <p v-if="!form.category_id" class="form-help">
          Select a category first
        </p>
        <p v-else-if="filteredSubcategories.length === 0" class="form-help">
          No subcategories available for this category
        </p>
      </div>

      <!-- Description Textarea -->
      <div class="form-group">
        <label for="edit-document-description" class="form-label">
          Description (Optional)
        </label>
        <textarea
          id="edit-document-description"
          v-model="form.description"
          class="form-textarea"
          rows="3"
          placeholder="Add a description for this document..."
          :disabled="isSaving"
        ></textarea>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <themed-button
          type="button"
          variant="outline"
          @click="handleCancel"
          :disabled="isSaving"
        >
          Cancel
        </themed-button>
        <themed-button
          type="submit"
          variant="default"
          :disabled="isSaving || !canSubmit"
        >
          <svg
            v-if="isSaving"
            class="animate-spin w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
        </themed-button>
      </div>
    </form>
  </themed-modal>
</template>

<script>
export default {
  name: 'DocumentEditModal',
  
  props: {
    document: {
      type: Object,
      default: null
    },
    categories: {
      type: Array,
      required: true
    },
    subcategories: {
      type: Array,
      required: true
    }
  },
  
  data() {
    return {
      form: {
        id: null,
        name: '',
        category_id: null,
        subcategory_id: null,
        description: ''
      },
      isSaving: false,
      errors: {},
      previouslyFocusedElement: null
    };
  },
  
  computed: {
    /**
     * Filter subcategories based on selected category
     */
    filteredSubcategories() {
      if (!this.form.category_id) {
        return [];
      }
      return this.subcategories.filter(sub => sub.category_id === this.form.category_id);
    },
    
    /**
     * Check if form can be submitted
     */
    canSubmit() {
      return this.form.name && this.form.name.trim() !== '' && this.form.category_id !== null;
    }
  },
  
  watch: {
    /**
     * Watch for document prop changes to populate form
     */
    document: {
      handler(newDocument) {
        if (newDocument) {
          this.populateForm(newDocument);
        }
      },
      immediate: true
    }
  },
  
  methods: {
    /**
     * Handle modal opened event
     */
    handleOpened() {
      // Store the element that had focus before modal opened
      this.previouslyFocusedElement = document.activeElement;
      
      // Populate form with document data
      if (this.document) {
        this.populateForm(this.document);
      }
      
      // Focus name input
      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
          this.$refs.nameInput.select();
        }
      });
      
      // Set up focus trap
      this.setupFocusTrap();
    },
    
    /**
     * Handle modal closed event
     */
    handleClosed() {
      // Reset form
      this.resetForm();
      
      // Restore focus to previously focused element
      if (this.previouslyFocusedElement) {
        this.$nextTick(() => {
          this.previouslyFocusedElement.focus();
        });
      }
      
      // Clean up focus trap
      this.removeFocusTrap();
    },
    
    /**
     * Populate form with document data
     */
    populateForm(document) {
      this.form = {
        id: document.id,
        name: document.name || '',
        category_id: document.category_id || null,
        subcategory_id: document.subcategory_id || null,
        description: document.description || ''
      };
      this.errors = {};
    },
    
    /**
     * Handle category change
     */
    handleCategoryChange() {
      // Clear subcategory if it doesn't belong to new category
      if (this.form.subcategory_id) {
        const subcategoryExists = this.filteredSubcategories.some(
          sub => sub.id === this.form.subcategory_id
        );
        if (!subcategoryExists) {
          this.form.subcategory_id = null;
        }
      }
      this.errors.category_id = '';
    },
    
    /**
     * Validate form
     */
    validateForm() {
      this.errors = {};
      
      if (!this.form.name || this.form.name.trim() === '') {
        this.errors.name = 'Document name is required';
        return false;
      }
      
      if (!this.form.category_id) {
        this.errors.category_id = 'Please select a category';
        return false;
      }
      
      return true;
    },
    
    /**
     * Handle form submission
     */
    async handleSubmit() {
      // Validate form
      if (!this.validateForm()) {
        return;
      }
      
      this.isSaving = true;
      
      try {
        // Build update payload
        const updates = {
          name: this.form.name.trim(),
          category_id: this.form.category_id,
          subcategory_id: this.form.subcategory_id,
          description: this.form.description ? this.form.description.trim() : ''
        };
        
        // Emit edit event to parent
        await this.$emit('edit', this.form.id, updates);
        
        // Close modal on success
        this.$modal.hide('document-edit-modal');
      } catch (error) {
        // Error is handled by parent component
        console.error('Edit error:', error);
      } finally {
        this.isSaving = false;
      }
    },
    
    /**
     * Handle cancel button
     */
    handleCancel() {
      this.$modal.hide('document-edit-modal');
    },
    
    /**
     * Reset form to initial state
     */
    resetForm() {
      this.form = {
        id: null,
        name: '',
        category_id: null,
        subcategory_id: null,
        description: ''
      };
      this.errors = {};
      this.isSaving = false;
    },
    
    /**
     * Set up focus trap for modal
     */
    setupFocusTrap() {
      this.handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          const modal = this.$el;
          if (!modal) return;
          
          const focusableElements = modal.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };
      
      document.addEventListener('keydown', this.handleKeyDown);
    },
    
    /**
     * Remove focus trap
     */
    removeFocusTrap() {
      if (this.handleKeyDown) {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
    }
  }
};
</script>
